import { Injectable, Injector, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { Environment } from '@qvs/core';
import { select, Store } from '@ngrx/store';
import { loadEnvironment, loadMockEnvironment, networkConnectionOff, networkConnectionOn, selectEnvFeature } from '../store';
import { filter, map, tap } from 'rxjs/operators';
import { PersistenceService } from '~/shared/services/persistence.service';
import { environment } from '#environment';
import { Device } from '@capacitor/device';
import { v4 as uuidv4 } from 'uuid';
import { ConfigurationEnum } from '@environments/IEnvironment';

@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  public environment: Environment;
  public disableAppPauseEmitter: boolean;
  // overwitten in tests
  // prettier-ignore
  private configuration = environment.configuration; // NOSONAR
  private appEnvironment = environment; // NOSONAR
  private userAgent = `${environment.appId}/${environment.appVersion}`;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly store: Store,
    private readonly injector: Injector,
    private readonly persistenceService: PersistenceService,
    private readonly ngZone: NgZone
  ) {
    this.calculateUserAgent();
  }

  async ready() {
    this.observeNetworkConnection();
    setTimeout(() => this.loadEnvironmentDependingOnContext());
    return firstValueFrom(
      this.store.pipe(
        select(selectEnvFeature),
        filter((state) => !!state.environment)
      )
    );
  }

  loadEnvironment(configName: string): Observable<Environment> {
    return this.httpClient.get(`assets/configuration/${configName}.json`).pipe(
      map((result: Environment) => ({ ...result, userAgent: this.userAgent })),
      tap((env) => (this.environment = env))
    );
  }

  loadAndApplyMockEnvironment(): Observable<Environment> {
    const mocks = this.appEnvironment?.mocks;
    if (mocks !== undefined) {
      // TODO 07-Mar-2023/braune: these mocks are probably better replaced using an API mock like msw:
      //  https://www.npmjs.com/package/msw
      this.persistenceService.loadMockState(mocks?.state);
      this.replaceWithMockServices(mocks?.services);
    }

    return this.loadEnvironment(ConfigurationEnum.UAT_LOCAL);
  }

  // TODO 09-Mar-2023/braune: instead of leveraging Angular DI, this replaces properties on the service objects themselves. By
  //  addressing the TODO above, we won't have to take care about this anymore.
  private replaceWithMockServices(injectors: { original: any; mock: any }[]) {
    injectors.forEach((replacement) => {
      try {
        Object.getOwnPropertyNames(this.injector.get<any>(replacement.original).__proto__).forEach((property) => {
          try {
            this.injector.get<any>(replacement.original)[property] = this.injector.get<any>(replacement.mock)[property];
          } catch (e) {
            // skip replacement for not existing mock injector
          }
        });
      } catch (e) {
        // skip replacement for not existing origin injector
      }
    });
  }

  private async calculateUserAgent(): Promise<void> {
    const deviceInfo = await Device.getInfo();
    this.userAgent = `${environment.appId}/${environment.appVersion}/${deviceInfo.operatingSystem}/${deviceInfo.manufacturer}/${
      deviceInfo.model
    }/${uuidv4()}`;
  }

  private loadEnvironmentDependingOnContext() {
    switch (this.configuration) {
      case ConfigurationEnum.MOCK_CRM_USER:
        this.store.dispatch(loadMockEnvironment({ mockType: ConfigurationEnum.MOCK_CRM_USER }));
        break;
      case ConfigurationEnum.MOCK_CLIENT:
        this.store.dispatch(loadMockEnvironment({ mockType: ConfigurationEnum.MOCK_CLIENT }));
        break;
      default:
        this.store.dispatch(loadEnvironment({ environmentConfiguration: this.configuration }));
    }
  }

  private observeNetworkConnection() {
    window.addEventListener('offline', () => {
      this.ngZone.run(() => this.store.dispatch(networkConnectionOff()));
    });

    window.addEventListener('online', () => {
      this.ngZone.run(() => this.store.dispatch(networkConnectionOn()));
    });
  }
}
